import { useState } from "react";

// [AUDIO PROCTORING v2] Shows the GPT cheating-analysis verdict over the
// candidate's proctoring-audio transcript, plus a collapsible full-transcript
// viewer. Renders whenever the analysis completed (including a "clear" verdict) or
// a transcript exists — "whether there were cheating instances or not".

const VERDICT_STYLES = {
    clear: { bg: "#e6f6ec", color: "#1e7a45", border: "#b7e3c6", label: "Clear" },
    inconclusive: { bg: "#fff4e0", color: "#b26a00", border: "#f5c88a", label: "Inconclusive" },
    suspicious: { bg: "#fdecec", color: "#c0392b", border: "#f5b7b1", label: "Suspicious" },
}

const AudioCheatingAnalysis = ({ audioProctoringReport }) => {
    const [showTranscript, setShowTranscript] = useState(false)

    const analysis = audioProctoringReport?.analysis
    const transcript = audioProctoringReport?.transcript
    const completed = audioProctoringReport?.status === "completed"
    const hasVerdict = !!(analysis && analysis.verdict)

    if (!completed || (!hasVerdict && !transcript)) return null

    const instances = Array.isArray(analysis?.instances) ? analysis.instances : []
    const style = VERDICT_STYLES[analysis?.verdict] || VERDICT_STYLES.inconclusive

    return (
        <section className="audio-cheating-analysis" style={{ marginTop: 16, marginBottom: 24 }}>
            <div className="behavioural-analysis--header">
                <h4>Audio cheating analysis</h4>
                <p>Automated review of the candidate&apos;s proctoring-audio transcript.</p>
            </div>

            {hasVerdict && (
                <>
                    <div className="flex items-center" style={{ gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                        <span style={{
                            display: "inline-flex", alignItems: "center",
                            background: style.bg, color: style.color, border: `1px solid ${style.border}`,
                            borderRadius: 16, padding: "3px 12px", fontSize: 13, fontWeight: 700, textTransform: "capitalize",
                        }}>
                            {style.label}
                        </span>
                        {analysis.confidence && (
                            <span style={{ fontSize: 12, color: "#8a8a8a" }}>confidence: {analysis.confidence}</span>
                        )}
                    </div>

                    {analysis.summary && (
                        <p style={{ marginTop: 10, color: "#444" }}>{analysis.summary}</p>
                    )}

                    {instances.length > 0 && (
                        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                            {instances.map((inst, index) => (
                                <article
                                    key={index}
                                    style={{
                                        borderLeft: `3px solid ${style.border}`, background: "#fafafa",
                                        borderRadius: 6, padding: "8px 12px",
                                    }}
                                >
                                    <p style={{ margin: 0, fontStyle: "italic", color: "#333" }}>&ldquo;{inst.quote}&rdquo;</p>
                                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#8a8a8a" }}>
                                        {inst.reason}{inst.severity ? ` · ${inst.severity}` : ""}
                                    </p>
                                </article>
                            ))}
                        </div>
                    )}
                </>
            )}

            {transcript && (
                <div style={{ marginTop: hasVerdict ? 16 : 8 }}>
                    <button
                        type="button"
                        onClick={() => setShowTranscript((v) => !v)}
                        style={{
                            background: "none", border: "none", padding: 0, cursor: "pointer",
                            color: "#b26a00", fontSize: 13, fontWeight: 600,
                        }}
                    >
                        {showTranscript ? "Hide full transcript" : "View full transcript"}
                    </button>
                    {showTranscript && (
                        <div style={{
                            marginTop: 8, maxHeight: 260, overflowY: "auto",
                            background: "#fafafa", border: "1px solid #eee", borderRadius: 8,
                            padding: "12px 14px", fontSize: 13.5, lineHeight: 1.6, color: "#333",
                            whiteSpace: "pre-wrap",
                        }}>
                            {transcript}
                        </div>
                    )}
                </div>
            )}
        </section>
    )
}

export default AudioCheatingAnalysis
